import { Action, combineReducers, configureStore } from "@reduxjs/toolkit";
import {
    FLUSH,
    PAUSE,
    PERSIST,
    PersistConfig,
    PersistedState,
    PersistMigrate,
    persistReducer,
    persistStore,
    PURGE,
    REGISTER,
    REHYDRATE,
} from "redux-persist";
import storage from "redux-persist/lib/storage";

import { APP_VERSION_PERSIST } from "@/config";
import { openReplayService } from "@/Services/OpenReplay";
import {
    fullReset,
    logoutResetApp,
    restoreConfigFromServer,
    restoreProgressFromServer,
    safeReset,
} from "@/store/actions";
import {
    configTrackingMiddleware,
    opfsQueueMiddleware,
    progressTrackingMiddleware,
    requestQueueTailListener,
} from "@/store/middleware";

import { authReducer } from "./slices/auth";
import { catalogReducer } from "./slices/catalog";
import { configReducer } from "./slices/config";
import { dictionariesReducer } from "./slices/dictionaries";
import { managerReducer } from "./slices/manager";
import { offlineReducer } from "./slices/offline";
import { progressReducer } from "./slices/progress";
import { themeReducer } from "./slices/theme";
import { uiReducer } from "./slices/ui";
import { userReducer } from "./slices/user";
import { workReducer } from "./slices/work";

export const appReducer = combineReducers({
    auth: authReducer,
    theme: themeReducer,
    user: userReducer,
    work: workReducer,
    manager: managerReducer,
    offline: offlineReducer,
    ui: uiReducer,
    config: configReducer,
    progress: progressReducer,
    dictionaries: dictionariesReducer,
    catalog: catalogReducer,
});

type AppState = ReturnType<typeof appReducer>;

const createResetState = (
    state: AppState | undefined,
    preserveConfig: {
        auth?: boolean;
        user?: boolean;
        progress?: boolean;
        offline?: boolean;
        work?: boolean;
        manager?: boolean;
    },
): AppState => {
    const initialState = appReducer(undefined, { type: "@@INIT" });
    const safeState = state ?? initialState;
    const resetState: AppState = { ...initialState };

    if (preserveConfig.auth && state?.auth) {
        resetState.auth = safeState.auth;
    }

    if (preserveConfig.user && state?.user) {
        resetState.user = safeState.user;
    }

    if (preserveConfig.work && state?.work) {
        resetState.work = safeState.work;
    }

    if (preserveConfig.progress && state?.progress) {
        resetState.progress = safeState.progress;
    }

    if (preserveConfig.manager && state?.manager) {
        resetState.manager = safeState.manager;
    }

    if (preserveConfig.offline && state?.offline) {
        resetState.offline = {
            ...initialState.offline,
            requestQueue: safeState.offline?.requestQueue,
            progress: safeState.offline?.progress,
        };
    }

    if (state?.theme) {
        resetState.theme = safeState.theme;
    }

    if (state?.config) {
        resetState.config = safeState.config;
    }

    return resetState;
};

export const rootReducer = (
    state: AppState | undefined,
    action: Action,
): AppState => {
    if (logoutResetApp.match(action)) {
        const resetState = createResetState(state, {
            auth: false,
            progress: true,
            offline: true,
        });
        return appReducer(resetState, action);
    }

    if (safeReset.match(action)) {
        const resetState = createResetState(state, {
            auth: true,
            user: true,
            progress: true,
            offline: true,
            work: true,
            manager: true,
        });
        return appReducer(resetState, action);
    }

    if (fullReset.match(action)) {
        const resetState = createResetState(state, {
            user: true,
            auth: true,
        });
        return appReducer(resetState, action);
    }

    if (restoreProgressFromServer.match(action)) {
        const { progress, requestQueue } = action.payload;

        const baseState = state ?? appReducer(undefined, { type: "@@INIT" });

        return {
            ...baseState,
            progress,
            offline: {
                ...baseState.offline,
                requestQueue,
            },
        };
    }

    if (restoreConfigFromServer.match(action)) {
        const config = action.payload;

        const baseState = state ?? appReducer(undefined, { type: "@@INIT" });

        return {
            ...baseState,
            config,
        };
    }

    return appReducer(state, action);
};

const migrate: PersistMigrate = async (state: PersistedState) => {
    if (!state || typeof state !== "object" || !("_persist" in state)) {
        return state;
    }

    const persistedVersion = state._persist.version;
    const shouldReset = persistedVersion !== APP_VERSION_PERSIST;

    if (shouldReset) {
        return {
            _versionChanged: true,
            _persist: {
                ...state._persist,
                version: APP_VERSION_PERSIST,
            },
        };
    }

    return state;
};

const persistConfig: PersistConfig<AppState> = {
    key: "root",
    storage,
    blacklist: ["auth", "catalog"],
    version: APP_VERSION_PERSIST,
    migrate,
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export function setupStore() {
    return configureStore({
        reducer: persistedReducer,
        middleware: (getDefaultMiddleware) =>
            getDefaultMiddleware({
                serializableCheck: {
                    ignoredActions: [
                        FLUSH,
                        REHYDRATE,
                        PAUSE,
                        PERSIST,
                        PURGE,
                        REGISTER,
                    ],
                },
            })
                .prepend(requestQueueTailListener.middleware)
                .concat(
                    progressTrackingMiddleware,
                    configTrackingMiddleware,
                    opfsQueueMiddleware,
                    openReplayService.reduxMiddleware,
                ),
    });
}

export const store = setupStore();
export const persistor = persistStore(store);
